import { existsSync, mkdirSync, readdirSync, readFileSync, rmSync, writeFileSync } from 'fs';
import { join } from 'path';
import { stringifyYaml } from './yaml.js';
import { BASE_META_FILE } from './workspace.js';
import {
  DEPRECATED_ENTITY_DIRS,
  ENTITY_DIRS,
  ENTITY_KINDS,
  type BaseConfig,
  type BaseMeta,
  type EntityKind,
} from './types.js';

/** Drop keys whose value is `undefined` so absent fields don't render as `null` in YAML. */
function omitUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as Partial<T>;
}

/** Strip every `auth.secret` from a record_type's sources — secrets are never written to disk. */
function stripRecordTypeSources(sources: unknown): unknown[] | undefined {
  if (!Array.isArray(sources)) return undefined;
  return sources.map((s) => {
    const source = s as Record<string, unknown>;
    const auth = source.auth as Record<string, unknown> | undefined;
    if (!auth) return source;
    const { secret: _secret, ...restAuth } = auth;
    return { ...source, auth: restAuth };
  });
}

/**
 * Build the ordered, secret-free file object for one entity. Field order is fixed so YAML diffs
 * stay minimal; `secret` is never included for inbound webhooks/triggers, and record_type source secrets are
 * stripped.
 */
function toFileObject(kind: EntityKind, entity: Record<string, unknown>): Record<string, unknown> {
  switch (kind) {
    case 'record_types':
      return omitUndefined({
        id: entity.id,
        name: entity.name,
        description: entity.description,
        icon: entity.icon,
        color: entity.color,
        json_schema: entity.json_schema,
        ui: entity.ui,
        sources: stripRecordTypeSources(entity.sources),
      });
    case 'relationship_types':
      return omitUndefined({
        id: entity.id,
        description: entity.description,
        data_schema: entity.data_schema,
        source_record_types: entity.source_record_types,
        target_record_types: entity.target_record_types,
        cascade: entity.cascade,
      });
    case 'inbound_webhooks':
      return omitUndefined({
        id: entity.id,
        name: entity.name,
        record_type_scope: entity.record_type_scope,
        // to_rekor mapping (#454 PR2) — secret-free, so it round-trips through config-as-code.
        field_mapping: entity.field_mapping,
        source_binding: entity.source_binding,
        // Static-header auth scheme (#470) — secret-free ({type,header}); the token reuses `secret`.
        ingest_auth: entity.ingest_auth,
        // Hydration config (#470 PR2) — secret-free; round-trips through config-as-code.
        hydration: entity.hydration,
        enabled: entity.enabled,
      });
    case 'triggers':
      return omitUndefined({
        id: entity.id,
        name: entity.name,
        // action is canonical (#390); url kept for human-readable webhook back-compat (dropped for internal_write).
        action: entity.action,
        url: entity.url,
        events: entity.events,
        record_type_scope: entity.record_type_scope,
        filter: entity.filter,
        skip_inbound_webhook_writes: entity.skip_inbound_webhook_writes,
        skip_cascade_writes: entity.skip_cascade_writes,
        enabled: entity.enabled,
      });
    case 'toolsets':
      return omitUndefined({
        id: entity.id,
        name: entity.name,
        description: entity.description,
        actions: entity.actions,
        relationships: entity.relationships,
        batch: entity.batch,
        sql_query: entity.sql_query,
      });
    case 'actions':
      return omitUndefined({
        id: entity.id,
        record_type: entity.record_type,
        operation: entity.operation,
        steps: entity.steps,
        description: entity.description,
        external_source: entity.external_source,
        writable_fields: entity.writable_fields,
        precondition: entity.precondition,
        binding: entity.binding,
        filterable_fields: entity.filterable_fields,
        expose_filter: entity.expose_filter,
        expose_sort: entity.expose_sort,
        default_sort: entity.default_sort,
        expose_limit: entity.expose_limit,
        default_limit: entity.default_limit,
        expose_offset: entity.expose_offset,
        expose_fields: entity.expose_fields,
        default_fields: entity.default_fields,
        agent_minimal: entity.agent_minimal,
      });
    case 'seeds':
      return omitUndefined({
        id: entity.id,
        description: entity.description,
        records: entity.records,
        relationships: entity.relationships,
        exclusive_record_types: entity.exclusive_record_types,
      });
  }
}

/** Remove `*.yaml` files in `dir` whose basename isn't in `keep` (reflect server-side deletions). */
function pruneOrphans(dir: string, keep: Set<string>): void {
  if (!existsSync(dir)) return;
  for (const f of readdirSync(dir)) {
    if (f.endsWith('.yaml') && !keep.has(f)) rmSync(join(dir, f));
  }
}

const META_ORDER: (keyof BaseMeta)[] = ['base_id', 'origin_base_id', 'name', 'description', 'environment', 'integrations'];

function metaFileObject(meta: BaseMeta): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of META_ORDER) {
    if (meta[k] !== undefined) out[k] = meta[k];
  }
  return out;
}

/**
 * Write a database folder: `base.yaml` plus one file per config entity (secrets stripped),
 * removing files for entities no longer present so the folder mirrors the server. Idempotent.
 */
export function writeBaseFolder(folder: string, meta: BaseMeta, config: BaseConfig): void {
  mkdirSync(folder, { recursive: true });
  writeFileSync(join(folder, BASE_META_FILE), stringifyYaml(metaFileObject(meta)));

  for (const kind of ENTITY_KINDS) {
    const dir = join(folder, ENTITY_DIRS[kind]);
    const entities: readonly unknown[] = config[kind] ?? [];
    const keep = new Set<string>();
    if (entities.length > 0) mkdirSync(dir, { recursive: true });
    for (const raw of entities) {
      const entity = raw as Record<string, unknown>;
      const id = String(entity.id);
      const file = `${id}.yaml`;
      keep.add(file);
      writeFileSync(join(dir, file), stringifyYaml(toFileObject(kind, entity)));
    }
    pruneOrphans(dir, keep);
  }

  // Remove dirs for kinds that no longer exist (e.g. `endpoints/` superseded by `toolsets/`, #562–567)
  // so a pull leaves a clean tree — the writer/parser ignore these dirs otherwise and they linger as
  // dead weight through every pull.
  for (const deprecated of DEPRECATED_ENTITY_DIRS) {
    rmSync(join(folder, deprecated), { recursive: true, force: true });
  }
}

/** Leading marker on a mirrored production `base.yaml`. YAML comments are ignored by the parser. */
const MIRROR_MARKER_PREFIX = '# Read-only mirror of production database';

/**
 * Prepend a read-only banner comment to a freshly written production-mirror `base.yaml` (a human
 * signal alongside the machine-readable `environment: production`). Idempotent: `writeBaseFolder`
 * rewrites the file fresh each time so the banner is normally re-added to a banner-less file, and the
 * prefix guard makes a stray double-call a no-op. No-op if `base.yaml` is missing.
 */
export function markProductionMirror(folder: string, baseId: string): void {
  const file = join(folder, BASE_META_FILE);
  if (!existsSync(file)) return;
  const body = readFileSync(file, 'utf-8');
  if (body.startsWith(MIRROR_MARKER_PREFIX)) return;
  const banner = `${MIRROR_MARKER_PREFIX} '${baseId}'. Edits are ignored; 'rekor push' refuses it. Edit the linked preview instead.\n`;
  writeFileSync(file, banner + body);
}

/** Content of the folder-local `.gitignore` written into a production-mirror folder: ignore everything. */
const MIRROR_GITIGNORE = '*\n';

/**
 * Git-ignore a read-only production-mirror folder so its regenerated contents never surface as
 * untracked/churning files in `git status` (issue #570). The mirror is a local, deterministically
 * regenerated reference — not authored content — so we treat it like build output. A folder-local
 * `.gitignore` of `*` ignores every file in the folder INCLUDING itself, leaving no non-ignored
 * content, so git hides the whole directory; it's self-cleaning (goes away if the mirror is deleted)
 * and never pollutes preview-iteration commits. Idempotent — the content is a constant. No-op if the
 * folder doesn't exist.
 */
export function ensureMirrorIgnored(folder: string): void {
  if (!existsSync(folder)) return;
  writeFileSync(join(folder, '.gitignore'), MIRROR_GITIGNORE);
}
